// CreateEvent.js
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TYPES = [
  { value: "KURS", label: "Kurs" },
  { value: "PERSONALMOTE", label: "Personalmøte" },
  { value: "MERKEDAG", label: "Merkedag" },
  { value: "FRIDAG", label: "Fridag" },
  { value: "KAMPANJE", label: "Kampanjeperiode" },
  { value: "STAND", label: "Stand" },
  { value: "BUTIKK", label: "Butikk" },
  { value: "ARRANGEMENT", label: "Arrangement" },
  { value: "EKSTERN", label: "Ekstern" },
];

const EXTERNAL_PARTY_OPTIONS = [
  "IL ROS",
  "Bødalen IF",
  "Slemmestad IF",
  "Hyggen IF",
  "Tofte Fremad",
  "Sætre Graabein",
  "Nærsnes IF",
  "Bamford1869",
  "Annet",
];

const STORE_SCOPE = [
  { value: "ROYKEN", label: "Sport 1 Røyken" },
  { value: "SLEMMESTAD", label: "Sport 1 Slemmestad" },
  { value: "BOTH", label: "Begge butikker" },
];

// Normaliser tekst til scope
const normalizeStoreScope = (val) => {
  const s = String(val || "").trim().toLowerCase();
  if (!s) return null;

  if (s === "both" || s.includes("begge")) return "BOTH";

  // Røyken-varianter
  if (s === "royken" || s.includes("røyken") || s.includes("royken")) return "ROYKEN";

  // Slemmestad-varianter
  if (s === "slemmestad" || s.includes("slemmestad")) return "SLEMMESTAD";

  // "Ingen" / tom
  if (s === "ingen" || s === "none" || s === "-") return null;

  return null;
};

// Mapper employee -> scope (støtter storeScope, butikkid og butikk (string))
const getEmployeeScope = (emp) => {
  // 1) storeScope på employee (ROYKEN/SLEMMESTAD/BOTH)
  const fromStoreScope = normalizeStoreScope(emp?.storeScope);
  if (fromStoreScope) return fromStoreScope;

  // 2) butikkid (1/2)
  if (String(emp?.butikkid) === "1") return "ROYKEN";
  if (String(emp?.butikkid) === "2") return "SLEMMESTAD";

  // 3) butikk (string) – f.eks. "Sport 1 Røyken" / "Sport 1 Slemmestad" / "Ingen"
  const fromButikk = normalizeStoreScope(emp?.butikk);
  if (fromButikk) return fromButikk;

  return null;
};

const employeeMatchesStoreScope = (emp, selectedScope) => {
  // BOTH = vis alle ansatte (også de med "Ingen")
  if (!selectedScope || selectedScope === "BOTH") return true;

  const empScope = getEmployeeScope(emp);

  // Ansatt uten butikk (f.eks "Ingen") -> skjul når en butikk er valgt
  if (!empScope) return false;

  // Ansatt som gjelder begge -> vis alltid
  if (empScope === "BOTH") return true;

  return empScope === selectedScope;
};

// Hjelper: finn mandag i gitt ISO-uke
function getDateOfISOWeek(week, year) {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay(); // 0=søndag, 1=mandag,...
  const ISOweekStart = new Date(simple);
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return ISOweekStart;
}

const schema = z
  .object({
    title: z.string().min(2, "Skriv et navn på eventet"),
    description: z.string().optional(),
    location: z.string().optional(),

    dateMode: z.enum(["single", "range", "weekRange"]).default("single"),
    allDay: z.boolean().default(false),

    start: z.string().optional(),
    end: z.string().optional(),
    weekStart: z.string().optional(),
    weekEnd: z.string().optional(),
    weekYear: z.string().optional(),

    type: z.enum(TYPES.map((t) => t.value)),

    externalPartyName: z
      .string()
      .optional()
      .refine((val) => !val || EXTERNAL_PARTY_OPTIONS.includes(val), {
        message: "Velg en gyldig ekstern part",
      }),

    inStore: z.boolean().default(false),
    storeScope: z.enum(STORE_SCOPE.map((s) => s.value)).default("BOTH"),

    outOfStoreSale: z.boolean().default(false),
    outOfStoreTurnover: z
      .string()
      .optional()
      .refine((val) => !val || !isNaN(Number(val)), {
        message: "Omsetning må være et tall",
      }),

    ownerId: z.string().min(1, "Velg ansvarlig"),
    employeesMode: z.enum(["ALLE", "NOEN"]).default("NOEN"),
    participantIds: z.array(z.string()).optional().default([]),

    // 👇 NYTT: timer pr deltaker (id -> string)
    participantHoursById: z
      .record(z.string(), z.string())
      .optional()
      .default({})
      .refine((obj) => {
        return Object.values(obj || {}).every(
          (v) => v === "" || (!isNaN(Number(v)) && Number(v) >= 0)
        );
      }, { message: "Timer må være et tall (0 eller mer)" }),
  })
  .refine(
    (data) =>
      data.dateMode !== "range" ||
      (data.dateMode === "range" && !!data.end && !!data.start),
    { path: ["end"], message: "Sluttidspunkt må settes for tidsperiode" }
  )
  .refine(
    (data) =>
      data.dateMode !== "weekRange" ||
      (data.weekStart && data.weekEnd && data.weekYear),
    { path: ["weekStart"], message: "Velg startuke, sluttuke og år" }
  )
  .refine(
    (data) => {
      const needsExternal = ["STAND", "ARRANGEMENT", "EKSTERN"].includes(data.type);
      if (!needsExternal) return true;
      return !!data.externalPartyName;
    },
    { path: ["externalPartyName"], message: "Velg ekstern part" }
  );

export default function EventForm({
  mode = "create",
  initialEvent,
  onCreated,
  onUpdated,
  defaultStoreScope = "BOTH",
}) {
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const from = params.get("from");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      dateMode: "single",
      allDay: false,
      location: "",
      storeScope: defaultStoreScope,
      employeesMode: "NOEN",
      weekYear: String(new Date().getFullYear()),
      participantIds: [],
      participantHoursById: {},

      outOfStoreSale: false,
      outOfStoreTurnover: "",
    },
  });

  const outOfStoreSale = watch("outOfStoreSale");
  const dateMode = watch("dateMode");
  const type = watch("type");
  const employeesMode = watch("employeesMode");
  const allDay = watch("allDay");
  const storeScope = watch("storeScope");

const watchedParticipantIds = watch("participantIds");
const watchedParticipantHoursById = watch("participantHoursById");

const participantIds = useMemo(
  () => watchedParticipantIds ?? [],
  [watchedParticipantIds]
);

const participantHoursById = useMemo(
  () => watchedParticipantHoursById ?? {},
  [watchedParticipantHoursById]
);


  const filteredEmployees = useMemo(() => {
    return (employees || []).filter((emp) => employeeMatchesStoreScope(emp, storeScope));
  }, [employees, storeScope]);

  // Toggle alle / fjern alle + timer
  const handleToggleAllParticipants = () => {
    const current = getValues("participantIds") || [];
    const ids = filteredEmployees.map((emp) => emp._id.toString());
    const allSelected = ids.length > 0 && ids.every((id) => current.includes(id));

    if (allSelected) {
      setValue("participantIds", []);
      setValue("participantHoursById", {});
    } else {
      setValue("participantIds", ids);
      const nextHours = { ...(getValues("participantHoursById") || {}) };
      ids.forEach((id) => {
        if (nextHours[id] == null) nextHours[id] = "";
      });
      setValue("participantHoursById", nextHours);
    }
  };

  // Synk timer med valgte ansatte
  useEffect(() => {
    const next = { ...(participantHoursById || {}) };

    // fjern timer for de som ikke er valgt
    Object.keys(next).forEach((id) => {
      if (!participantIds.includes(id)) delete next[id];
    });

    // legg til tom key for nye valgte
    participantIds.forEach((id) => {
      if (next[id] == null) next[id] = "";
    });

    const changed =
      Object.keys(next).length !== Object.keys(participantHoursById).length ||
      Object.keys(next).some((k) => next[k] !== participantHoursById[k]);

    if (changed) setValue("participantHoursById", next);
  }, [participantIds, participantHoursById, setValue]);

  // Hent ansatte
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoadingEmployees(true);
        const res = await fetch(`${API_BASE_URL}/employees`);
        const data = await res.json();
        setEmployees(Array.isArray(data) ? data : []);

        // Default ansvarlig fra cookie i create
        if (mode === "create") {
          const selectedEmployeeName = Cookies.get("selectedEmployee");
          if (selectedEmployeeName && Array.isArray(data)) {
            const match = data.find((emp) => emp.navn === selectedEmployeeName);
            if (match) {
              setValue("ownerId", match._id.toString());
              if (match.butikkid) Cookies.set("butikkid", match.butikkid);
            }
          }
        }
      } catch (e) {
        console.error("Kunne ikke hente ansatte", e);
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [mode, setValue]);

  // Rydd participantIds/ownerId når butikkfilter endrer seg
  useEffect(() => {
    if (!employees || employees.length === 0) return;

    const allowedIds = new Set(filteredEmployees.map((e) => e._id.toString()));

    const currentIds = getValues("participantIds") || [];
    const nextIds = currentIds.filter((id) => allowedIds.has(String(id)));
    if (nextIds.length !== currentIds.length) {
      setValue("participantIds", nextIds);
    }

    const ownerId = getValues("ownerId");
    if (ownerId && !allowedIds.has(String(ownerId))) {
      setValue("ownerId", "");
    }
  }, [storeScope, employees, filteredEmployees, getValues, setValue]);

  // Prefill dato når vi kommer fra kalenderen i create-modus (?date=)
  useEffect(() => {
    if (mode !== "create") return;
    if (initialEvent) return;

    const p = new URLSearchParams(location.search);
    const dateStr = p.get("date");
    if (!dateStr) return;

    const date = new Date(dateStr + "T10:00");
    const localInputValue = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setValue("dateMode", "single");
    setValue("start", localInputValue);
  }, [location, mode, initialEvent, setValue]);

  // Prefill i edit-mode (første runde)
  useEffect(() => {
    if (mode === "edit" && initialEvent) {
      const start = initialEvent.start ? new Date(initialEvent.start) : null;
      const end = initialEvent.end ? new Date(initialEvent.end) : null;

      const toLocalInput = (d) =>
        d
          ? new Date(d.getTime() - d.getTimezoneOffset() * 60000)
              .toISOString()
              .slice(0, 16)
          : "";

      const dateModeValue =
        start && end && start.getTime() !== end.getTime() ? "range" : "single";

      const tagType = initialEvent.tags?.[0];
      const reverseCategory = initialEvent.category;
      let typeValue = tagType;
      if (!typeValue) {
        switch (reverseCategory) {
          case "STAND":
            typeValue = "STAND";
            break;
          case "KAMPANJE":
            typeValue = "KAMPANJE";
            break;
          case "MERKEDAG":
            typeValue = "MERKEDAG";
            break;
          case "FRIDAG":
            typeValue = "FRIDAG";
            break;
          case "INTERN":
            typeValue = "PERSONALMOTE";
            break;
          default:
            typeValue = "BUTIKK";
        }
      }

      reset({
        title: initialEvent.title || "",
        description: initialEvent.description || "",
        location: initialEvent.location || "",
        dateMode: dateModeValue,
        allDay: !!initialEvent.allDay,
        start: start ? toLocalInput(start) : "",
        end: dateModeValue === "range" && end ? toLocalInput(end) : "",
        type: typeValue,
        externalPartyName: initialEvent.locationHint?.externalPartyName || "",
        inStore: !!initialEvent.locationHint?.inStore,
        storeScope: initialEvent.storeScope || defaultStoreScope,
        ownerId: initialEvent.ownerId ? initialEvent.ownerId.toString() : "",
        employeesMode: initialEvent.participants?.mode || "NOEN",
        participantIds: (initialEvent.participants?.ids || []).map(String),
        participantHoursById: Object.fromEntries(
          Object.entries(initialEvent.participants?.hoursById || {}).map(([k, v]) => [
            String(k),
            String(v),
          ])
        ),
        weekYear: String(start ? start.getFullYear() : new Date().getFullYear()),

        outOfStoreSale: !!initialEvent.locationHint?.outOfStoreSale,
        outOfStoreTurnover: initialEvent.locationHint?.outOfStoreTurnover
          ? String(initialEvent.locationHint.outOfStoreTurnover)
          : "",
      });
    }
  }, [mode, initialEvent, reset, defaultStoreScope]);

  // Etter både ansatte og initialEvent er klare → prefill på nytt
  useEffect(() => {
    if (mode === "edit" && initialEvent && employees.length > 0) {
      reset((current) => ({
        ...current,
        ownerId: initialEvent.ownerId ? initialEvent.ownerId.toString() : "",
        employeesMode: initialEvent.participants?.mode || "NOEN",
        participantIds: (initialEvent.participants?.ids || []).map(String),
        participantHoursById: Object.fromEntries(
          Object.entries(initialEvent.participants?.hoursById || {}).map(([k, v]) => [
            String(k),
            String(v),
          ])
        ),
      }));
    }
  }, [mode, initialEvent, employees, reset]);

  const buildPayload = (values) => {
    const categoryMap = {
      KURS: "INTERN",
      PERSONALMOTE: "INTERN",
      MERKEDAG: "MERKEDAG",
      FRIDAG: "FRIDAG",
      KAMPANJE: "KAMPANJE",
      STAND: "STAND",
      BUTIKK: "AKTIVITET",
      ARRANGEMENT: "AKTIVITET",
      EKSTERN: "AKTIVITET",
    };

    const selectedEmployeeName = Cookies.get("selectedEmployee") || null;
    const butikkidCookie = Cookies.get("butikkid") || null;

    let startIso = null;
    let endIso = null;
    let allDayFinal = values.allDay;

    if (values.dateMode === "weekRange") {
      const year = Number(values.weekYear);
      const wStart = Number(values.weekStart);
      const wEnd = Number(values.weekEnd);

      const startDate = getDateOfISOWeek(wStart, year);
      const endWeekMonday = getDateOfISOWeek(wEnd, year);
      const endDate = new Date(endWeekMonday);
      endDate.setDate(endDate.getDate() + 6);

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      startIso = startDate.toISOString();
      endIso = endDate.toISOString();
      allDayFinal = true;
    } else {
      startIso = values.start ? new Date(values.start).toISOString() : null;
      endIso =
        values.dateMode === "single"
          ? startIso
          : values.end
          ? new Date(values.end).toISOString()
          : null;
    }

    return {
      title: values.title,
      description: values.description,
      location: values.location || "",
      start: startIso,
      end: endIso || startIso,
      allDay: allDayFinal,
      category: categoryMap[values.type] || "AKTIVITET",
      storeScope: values.storeScope,

      locationHint: {
        inStore: values.inStore,
        eventType: values.type,
        externalPartyName: values.externalPartyName || null,
        outOfStoreSale: values.outOfStoreSale || false,
        outOfStoreTurnover: values.outOfStoreTurnover ? Number(values.outOfStoreTurnover) : null,
      },

      ownerId: values.ownerId,

      participants:
        values.employeesMode === "ALLE"
          ? { mode: "ALLE", ids: [], hoursById: {} }
          : {
              mode: "NOEN",
              ids: values.participantIds || [],
              hoursById: Object.fromEntries(
                Object.entries(values.participantHoursById || {}).map(([id, v]) => [
                  id,
                  v === "" || v == null ? 0 : Number(v),
                ])
              ),
            },

      status: initialEvent?.status || "PLANNED",
      tags: [values.type],

      employeeContext: {
        selectedEmployeeName,
        butikkid: butikkidCookie,
      },
    };
  };

  const goBackToCalendar = () => {
    if (from) navigate(`/eventcalendar?date=${from}`);
    else navigate("/eventcalendar");
  };

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      const payload = buildPayload(values);

      const url =
        mode === "edit" && initialEvent?._id
          ? `${API_BASE_URL}/events/${initialEvent._id}`
          : `${API_BASE_URL}/events`;

      const method = mode === "edit" ? "PATCH" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Feil ved lagring (${res.status})`);
      const result = await res.json();

      if (mode === "edit") onUpdated?.(result);
      else onCreated?.(result);

      alert("Eventet er lagret 👌");
      goBackToCalendar();
    } catch (e) {
      alert(e.message || "Noe gikk galt ved lagring av event");
    } finally {
      setSubmitting(false);
    }
  };

  const titleText = mode === "edit" ? "Rediger event" : "Registrer event";
  const buttonText = submitting
    ? mode === "edit"
      ? "Oppdaterer…"
      : "Lagrer…"
    : mode === "edit"
    ? "Oppdater event"
    : "Lagre event";

  return (
    <div className="max-w-5xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6 mb-4">
      <div className="border border-gray-200 rounded-lg p-4">
        <h2 className="text-3xl font-bold mb-2 text-center">{titleText}</h2>
        <p className="text-sm text-gray-600 mb-6 text-center">
          Årshjul for aktiviteter, stands og kampanjer i butikk og ute hos klubb.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Grunninfo */}
          <section>
            <h3 className="text-lg font-semibold mb-3 border-b pb-1">Grunninformasjon</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Navn på event</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-gray-300 p-2"
                  placeholder="F.eks. Stand hos Bødalen IF"
                  {...register("title")}
                />
                {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium">Type</label>
                <select className="mt-1 w-full rounded-md border border-gray-300 p-2 bg-white" {...register("type")}>
                  <option value="">Velg type</option>
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {errors.type && <p className="text-sm text-red-600 mt-1">{errors.type.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium">
                  Navn på ekstern part (klubb/forening/person)
                </label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 p-2 bg-white"
                  {...register("externalPartyName")}
                >
                  <option value="">Velg ekstern part</option>
                  {EXTERNAL_PARTY_OPTIONS.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>

                {["STAND", "ARRANGEMENT", "EKSTERN"].includes(type) && errors.externalPartyName && (
                  <p className="text-sm text-red-600 mt-1">{errors.externalPartyName.message}</p>
                )}
              </div>
            </div>
          </section>

          {/* Butikk / sted */}
          <section>
            <h3 className="text-lg font-semibold mb-3 border-b pb-1">Butikk og sted</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
              <div className="flex items-center gap-2">
                <input id="inStore" type="checkbox" className="h-4 w-4" {...register("inStore")} />
                <label htmlFor="inStore" className="text-sm font-medium">I butikk</label>
              </div>

              <div>
                <label className="block text-sm font-medium">Butikk-tilhørighet</label>
                <select className="mt-1 w-full rounded-md border border-gray-300 p-2 bg-white" {...register("storeScope")}>
                  {STORE_SCOPE.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium">Sted (valgfritt)</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-md border border-gray-300 p-2"
                  placeholder="F.eks. Idrettsplassen, Slemmestad IF"
                  {...register("location")}
                />
              </div>

              <div className="md:col-span-2 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <input id="outOfStoreSale" type="checkbox" className="h-4 w-4" {...register("outOfStoreSale")} />
                  <label htmlFor="outOfStoreSale" className="text-sm font-medium">Salg utenfor butikk</label>
                </div>

                {outOfStoreSale && (
                  <div className="flex items-center gap-2">
                    <label htmlFor="outOfStoreTurnover" className="text-sm font-medium">Omsetning (kr)</label>
                    <input
                      id="outOfStoreTurnover"
                      type="number"
                      min="0"
                      step="1"
                      className="mt-1 w-40 rounded-md border border-gray-300 p-2"
                      {...register("outOfStoreTurnover")}
                    />
                  </div>
                )}
              </div>

              {errors.outOfStoreTurnover && (
                <p className="text-sm text-red-600 mt-1 md:col-span-2">{errors.outOfStoreTurnover.message}</p>
              )}
            </div>
          </section>

          {/* Tidspunkt */}
          <section>
            <h3 className="text-lg font-semibold mb-3 border-b pb-1">Tidspunkt</h3>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <input type="radio" id="dateModeSingle" value="single" {...register("dateMode")} />
                  <label htmlFor="dateModeSingle" className="text-sm">Enkelt dag/tid</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="radio" id="dateModeRange" value="range" {...register("dateMode")} />
                  <label htmlFor="dateModeRange" className="text-sm">Periode (fra–til)</label>
                </div>
                <div className="flex items-center gap-2">
                  <input type="radio" id="dateModeWeekRange" value="weekRange" {...register("dateMode")} />
                  <label htmlFor="dateModeWeekRange" className="text-sm">Ukebasert periode (f.eks. uke 51–52)</label>
                </div>
                <div className="flex items-center gap-2">
                  <input id="allDay" type="checkbox" {...register("allDay")} />
                  <label htmlFor="allDay" className="text-sm">Heldags</label>
                </div>
              </div>

              {dateMode === "weekRange" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium">Start uke</label>
                      <input type="number" min="1" max="53" className="mt-1 w-full rounded-md border border-gray-300 p-2" {...register("weekStart")} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium">Slutt uke</label>
                      <input type="number" min="1" max="53" className="mt-1 w-full rounded-md border border-gray-300 p-2" {...register("weekEnd")} />
                    </div>

                    <div>
                      <label className="block text-sm font-medium">År</label>
                      <input type="number" min="2024" max="2035" className="mt-1 w-full rounded-md border border-gray-300 p-2" {...register("weekYear")} />
                    </div>
                  </div>

                  {errors.weekStart && <p className="text-sm text-red-600 mt-1">{errors.weekStart.message}</p>}

                  <p className="text-xs text-gray-600">
                    Vi setter automatisk start til mandag i startuken og slutt til søndag i sluttuken (hele dager).
                  </p>
                </>
              )}

              {dateMode !== "weekRange" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">Start</label>
                    <input type="datetime-local" className="mt-1 w-full rounded-md border border-gray-300 p-2" {...register("start")} />
                    {errors.start && <p className="text-sm text-red-600 mt-1">Starttidspunkt mangler</p>}
                  </div>

                  {dateMode === "range" && (
                    <div>
                      <label className="block text-sm font-medium">Slutt</label>
                      <input type="datetime-local" className="mt-1 w-full rounded-md border border-gray-300 p-2" {...register("end")} />
                      {errors.end && <p className="text-sm text-red-600 mt-1">{errors.end.message}</p>}
                    </div>
                  )}
                </div>
              )}

              {allDay && dateMode !== "weekRange" && (
                <p className="text-xs text-gray-600">Heldags ignorerer klokkeslett, men datoen brukes.</p>
              )}
            </div>
          </section>

          {/* Ansvar og bemanning */}
          <section>
            <h3 className="text-lg font-semibold mb-3 border-b pb-1">Ansvar og bemanning</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Ansvarlig */}
              <div>
                <label className="block text-sm font-medium">Ansvarlig</label>
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 p-2 bg-white"
                  {...register("ownerId", {
                    onChange: (e) => {
                      const value = e.target.value;
                      const selectedEmployee = filteredEmployees.find((emp) => emp._id.toString() === value);
                      if (selectedEmployee) {
                        Cookies.set("selectedEmployee", selectedEmployee.navn);
                        if (selectedEmployee.butikkid) Cookies.set("butikkid", selectedEmployee.butikkid);
                      }
                    },
                  })}
                >
                  <option value="">Velg ansvarlig…</option>
                  {filteredEmployees.map((emp) => (
                    <option key={emp._id} value={emp._id}>{emp.navn}</option>
                  ))}
                </select>

                {errors.ownerId && <p className="text-sm text-red-600 mt-1">{errors.ownerId.message}</p>}
                {loadingEmployees && <p className="text-xs text-gray-500 mt-1">Laster ansatte…</p>}
              </div>

              {/* Bemanning */}
              <div>
                <label className="block text-sm font-medium">Ansatte</label>
                <div className="flex items-center gap-4 mt-1">
                  <div className="flex items-center gap-2">
                    <input type="radio" id="empAlle" value="ALLE" {...register("employeesMode")} />
                    <label htmlFor="empAlle" className="text-sm">Alle</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="radio" id="empNoen" value="NOEN" {...register("employeesMode")} />
                    <label htmlFor="empNoen" className="text-sm">Noen</label>
                  </div>
                </div>

                {employeesMode === "NOEN" && (
                  <div className="mt-2 border border-gray-300 rounded-md p-2 bg-white max-h-72 overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500">Huk av ansatte som skal delta</span>
                      <button type="button" onClick={handleToggleAllParticipants} className="text-xs underline text-blue-600">
                        Velg alle / fjern alle
                      </button>
                    </div>

                    {/* ✅ Avhuking + timer */}
                    <div className="grid grid-cols-1 gap-2">
{filteredEmployees.map((emp) => {
  const id = emp._id.toString();
  const checked = participantIds.includes(id);

  return (
    <div
      key={id}
      className="flex items-center justify-between gap-3 border rounded-md px-2 py-2"
    >
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          value={id}
          {...register("participantIds")}
          className="h-4 w-4"
        />
        <span>{emp.navn}</span>
      </label>

      {/* 👇 Vis KUN timer-felt når ansatt er avhuket */}
      {checked && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Timer</span>
          <input
            type="number"
            min="0"
            step="0.25"
            className="w-24 rounded-md border border-gray-300 p-2 text-sm bg-white"
            value={participantHoursById[id] ?? ""}
            onChange={(e) => {
              setValue("participantHoursById", {
                ...(participantHoursById || {}),
                [id]: e.target.value,
              });
            }}
          />
        </div>
      )}
    </div>
  );
})}

                    </div>

                    {errors.participantHoursById && (
                      <p className="text-sm text-red-600 mt-2">{errors.participantHoursById.message}</p>
                    )}
                  </div>
                )}

                {employeesMode === "NOEN" && errors.participantIds && (
                  <p className="text-sm text-red-600 mt-1">{errors.participantIds.message}</p>
                )}
              </div>
            </div>
          </section>

          {/* Kommentar */}
          <section>
            <h3 className="text-lg font-semibold mb-3 border-b pb-1">Kommentar</h3>
            <div>
              <label className="block text-sm font-medium">Kommentar / beskrivelse</label>
              <textarea
                rows={4}
                className="mt-1 w-full rounded-md border border-gray-300 p-2"
                placeholder="Kort om innhold, behov, erfaringer du ønsker å fange…"
                {...register("description")}
              />
            </div>
          </section>

          {/* Knapper */}
          <div className="flex flex-wrap gap-3 justify-end border-t pt-4 mt-2">
            <button type="button" onClick={goBackToCalendar} className="rounded-md px-4 py-2 border border-gray-300 text-sm">
              Avbryt
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md px-5 py-2 text-sm bg-green-500 text-white hover:bg-green-600 disabled:opacity-60"
            >
              {buttonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
